import * as ngCommon from '@angular/common'
import * as ngCore from '@angular/core'

import * as models from '../models'

import * as overlay from './overlay'
import { AtnDsSelectMultipleStore } from './store'

/**
 * Basic multiple select input.
 *
 * **Inputs**
 * - {@link Component.itemsSg items}!
 * - {@link Component.selectedSg selected}!
 * - {@link Component.loadingSg loading}?
 * - {@link Component.labelSg label}?
 * - {@link Component.configSg config}?
 * - {@link Component.disabledSg disabled}?
 *
 * **Outputs**
 * - {@link Component.valueSg | valueChange}
 *
 * @example
 * ```html
 * <atn-ds-select-multiple
 *   [items]="items"
 *   [value]="selected"
 *   [loading]="false"
 *   [label]="'Multi Select Input'"
 *   [config]="config"
 *   [disabled]="false"
 *   (valueChange)="handleChange($event)"
 * />
 * ```
 */
@ngCore.Component({
  imports: [ngCommon.NgClass, overlay.Component],
  providers: [AtnDsSelectMultipleStore],
  selector: 'atn-ds-select-multiple',
  standalone: true,
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component<TValue> {
  /** @see {@link AtnDsSelectMultipleStore} */
  public readonly store = ngCore.inject(AtnDsSelectMultipleStore<TValue>)
  /** TODO:  */
  public selectedItemsRefSg = ngCore.viewChildren<
    ngCore.ElementRef<HTMLElement>
  >('selectedItemRef', {})
  /** TODO:  */
  public readonly selectItemsContainerRefSg = ngCore.viewChild<
    ngCore.ElementRef<HTMLElement>
  >('selectItemsContainerRef')
  /** Items to display in the select */
  public readonly itemsSg = ngCore.input<readonly models.Item<TValue>[]>([], {
    alias: 'items',
  })
  /** Selected items model */
  public readonly valueSg = ngCore.model<
    readonly models.Item<TValue>['value'][]
  >([], {
    alias: 'value',
  })
  /** Label to display above the select */
  public readonly label = ngCore.input<string | undefined>(undefined, {
    alias: 'label',
  })
  /** @see {@link models.Config} */
  public readonly configSg = ngCore.input<models.Config | undefined>(
    undefined,
    { alias: 'config' },
  )
  /** Whether the select is loading */
  public readonly loadingSg = ngCore.input<boolean | undefined>(false, {
    alias: 'loading',
  })
  /** TODO:  */
  public readonly errorSg = ngCore.input<Error | string | undefined>(
    undefined,
    {
      alias: 'error',
    },
  )
  /** Whether the select is disabled */
  public readonly disabledSg = ngCore.input<boolean | undefined>(false, {
    alias: 'disabled',
  })
  /** Placeholder text to display when no item is selected. */
  public readonly placeholderSg = ngCore.input<string | undefined>(undefined, {
    alias: 'placeholder',
  })
  /**
   * If we have more items then can fit in the select, we show the expand button.
   */
  public readonly showExpandButtonSg = ngCore.computed(() => {
    const selectedItemsRef = this.selectedItemsRefSg()
    const selectItemsContainerRef = this.selectItemsContainerRefSg()
    const childrenWidth =
      selectedItemsRef?.reduce(
        (prev, curr) => prev + curr.nativeElement.offsetWidth,
        0,
      ) ?? 0
    const offsetWidth = selectItemsContainerRef?.nativeElement.offsetWidth ?? 0
    return childrenWidth > offsetWidth
  })

  /**
   * Emits an event when the "refresh" arrow on the error banner is clicked,
   * while in the {@link errorSg} state.
   *
   * __NOTE__: Error button will be present only if this handler is defined
   */
  // Can't use 'ngCore.output', because it doesn't have 'observed' property
  @ngCore.Output()
  public readonly refetch = new ngCore.EventEmitter<void>()

  constructor() {
    /** @see {@link itemsSg} */
    ngCore.effect(() => {
      this.store.patchState({ items: this.itemsSg() })
    })
    /** @see {@link valueSg} */
    ngCore.effect(() => {
      this.store.patchState({ selectedValues: this.valueSg() })
    })
    /** Updates {@link valueSg} to support two way bindings */
    ngCore.effect(() => {
      const isOpen = this.store.isOpenSg()
      const selectedValues = this.store.selectedValuesSg()

      // TODO: skip initial emissions
      if (!isOpen) {
        this.valueSg.set(selectedValues)
      }
    })
    /** @see {@link labelSg} */
    ngCore.effect(() => {
      this.store.patchState({ label: this.label() })
    })
    /** @see {@link configSg} */
    ngCore.effect(() => {
      const config = this.configSg()

      // TODO: this was just copy pasted, evaluate the possibility of using
      // Object.assign.
      this.store.patchState(state => ({
        ...state,
        config: {
          ...state.config,
          ...config,
          overlay: {
            ...state.config.overlay,
            ...config?.overlay,
          },
        },
      }))
    })
    /** @see {@link loadingSg} */
    ngCore.effect(() => {
      this.store.patchState({ isLoading: this.loadingSg() })
    })
    /** @see {@link errorSg} */
    ngCore.effect(() => {
      this.store.patchState({ error: this.errorSg() })
    })
    /** @see {@link disabledSg} */
    ngCore.effect(() => {
      this.store.patchState({ disabled: this.disabledSg() })
    })
    /** @see {@link placeholderSg} */
    ngCore.effect(() => {
      this.store.patchState({ placeholder: this.placeholderSg() })
    })
  }

  /** TODO:  */
  public toggleExpandButtonClickHandler(): void {
    const expanded = this.store.expandedSg()

    this.store.patchState({ expanded: !expanded })
  }

  public toggleDropdownClickHandler(): void {
    const isOpen = this.store.isOpenSg()

    this.store.patchState({ isOpen: !isOpen })
  }
}
